import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const { language, t } = useLanguage();

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact_address'),
      lines: ['Amitalia e.V.', 'Salinenstraße 40', '76889 Karlsdorf-Neuthard', language === 'de' ? 'Deutschland' : 'Germania'],
    },
    {
      icon: Mail,
      title: t('contact_email'),
      lines: ['info@amitalia.de'],
    },
  ];

  return (
    <div className="overflow-hidden" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6" data-testid="contact-title">
              {t('contact_title')}
            </h1>
            <p className="text-lg text-foreground/70 max-w-2xl">
              {t('contact_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {contactInfo.map((info, index) => (
                <div key={index} className="flex gap-4" data-testid={`contact-info-${index}`}>
                  <div className="w-12 h-12 bg-ferrari/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-5 h-5 text-ferrari" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">{info.title}</h3>
                    {info.lines.map((line, i) => (
                      <p key={i} className="text-foreground/60 text-sm">{line}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Address Card */}
              <div className="mt-12">
                <div className="bg-[#F5F5F7] p-8 luxury-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-ferrari/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-ferrari" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold mb-2">
                        {language === 'de' ? 'Unser Standort' : 'La nostra sede'}
                      </h3>
                      <p className="text-foreground/70 text-sm leading-relaxed">
                        Amitalia e.V.<br />
                        Salinenstra&szlig;e 40<br />
                        76889 Karlsdorf-Neuthard<br />
                        {language === 'de' ? 'Deutschland' : 'Germania'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Legal Section */}
      <section className="py-20 bg-[#F5F5F7]" data-testid="legal-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Impressum */}
            <div>
              <h2 className="font-heading text-2xl font-semibold mb-6">{t('footer_imprint')}</h2>
              <div className="text-foreground/70 space-y-2 text-sm">
                <p><strong>Amitalia e.V.</strong></p>
                <p>Salinenstraße 40</p>
                <p>76889 Karlsdorf-Neuthard</p>
                <p>{language === 'de' ? 'Deutschland' : 'Germania'}</p>
                <p className="pt-4">{language === 'de' ? 'Vertretungsberechtigter Vorstand:' : 'Rappresentante legale:'}</p>
                <p>Aurelio Marrelli ({language === 'de' ? 'Vorsitzender' : 'Presidente'})</p>
                <p>Dr. Alessandro Bellardita ({language === 'de' ? 'Stellv. Vorsitzender' : 'Vice Presidente'})</p>
              </div>
            </div>

            {/* Datenschutzerklaerung */}
            <div id="datenschutz">
              <h2 className="font-heading text-2xl font-semibold mb-6">{t('footer_privacy')}</h2>
              <div className="text-foreground/70 space-y-4 text-sm">
                <p className="font-semibold text-foreground">
                  {language === 'de' ? '1. Verantwortlicher' : '1. Titolare del trattamento'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Amitalia e.V., Salinenstraße 40, 76889 Karlsdorf-Neuthard, Deutschland. E-Mail: datenschutz@amitalia.de'
                    : 'Amitalia e.V., Salinenstraße 40, 76889 Karlsdorf-Neuthard, Germania. E-mail: datenschutz@amitalia.de'}
                </p>

                <p className="font-semibold text-foreground">
                  {language === 'de' ? '2. Erhebung und Speicherung personenbezogener Daten' : '2. Raccolta e conservazione dei dati personali'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Beim Besuch unserer Website werden automatisch Informationen an den Server unserer Website gesendet. Diese Informationen werden temporär in einem sog. Logfile gespeichert. Es werden keine externen Dienste (wie Google Fonts, Google Maps, Analyse-Tools oder externe Bilder) eingebunden. Alle Ressourcen werden lokal bereitgestellt.'
                    : 'Quando visitate il nostro sito web, le informazioni vengono automaticamente inviate al server del nostro sito. Queste informazioni vengono temporaneamente memorizzate in un cosiddetto file di log. Non vengono utilizzati servizi esterni (come Google Fonts, Google Maps, strumenti di analisi o immagini esterne). Tutte le risorse sono fornite localmente.'}
                </p>

                <p className="font-semibold text-foreground">
                  {language === 'de' ? '3. Kontaktformular und Mitgliedsantrag' : '3. Modulo di contatto e richiesta di iscrizione'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Die von Ihnen im Kontaktformular oder Mitgliedsantrag eingegebenen Daten (Name, E-Mail, Adresse etc.) werden ausschließlich zur Bearbeitung Ihrer Anfrage bzw. Ihres Antrags verwendet. Rechtsgrundlage ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) bzw. Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung). Die Daten werden nach Erledigung des Zwecks gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.'
                    : 'I dati inseriti nel modulo di contatto o nella richiesta di iscrizione (nome, e-mail, indirizzo, ecc.) vengono utilizzati esclusivamente per elaborare la vostra richiesta. La base giuridica è l\'art. 6 par. 1 lett. a GDPR (consenso) e l\'art. 6 par. 1 lett. b GDPR (esecuzione del contratto). I dati vengono cancellati dopo il raggiungimento dello scopo, salvo obblighi legali di conservazione.'}
                </p>

                <p className="font-semibold text-foreground">
                  {language === 'de' ? '4. Lokale Speicherung (localStorage)' : '4. Archiviazione locale (localStorage)'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Unsere Website speichert Ihre Spracheinstellung (Deutsch/Italienisch) im localStorage Ihres Browsers. Dies ist technisch notwendig für die Funktionalität der Website und erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse). Es werden keine Cookies gesetzt und keine Tracking-Daten erhoben.'
                    : 'Il nostro sito web memorizza la vostra preferenza linguistica (tedesco/italiano) nel localStorage del browser. Questo è tecnicamente necessario per la funzionalità del sito e avviene sulla base dell\'art. 6 par. 1 lett. f GDPR (interesse legittimo). Non vengono impostati cookie e non vengono raccolti dati di tracciamento.'}
                </p>

                <p className="font-semibold text-foreground">
                  {language === 'de' ? '5. Keine Weitergabe an Dritte' : '5. Nessuna trasmissione a terzi'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Ihre Daten werden nicht an Dritte weitergegeben, es sei denn, wir sind gesetzlich dazu verpflichtet. Es findet keine Datenübermittlung in Drittländer statt.'
                    : 'I vostri dati non vengono trasmessi a terzi, salvo obbligo di legge. Non avviene alcun trasferimento di dati verso paesi terzi.'}
                </p>

                <p className="font-semibold text-foreground">
                  {language === 'de' ? '6. Ihre Rechte' : '6. I vostri diritti'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16 DSGVO), Löschung (Art. 17 DSGVO), Einschränkung der Verarbeitung (Art. 18 DSGVO), Datenübertragbarkeit (Art. 20 DSGVO) und Widerspruch (Art. 21 DSGVO). Zudem haben Sie das Recht, sich bei einer Aufsichtsbehörde zu beschweren.'
                    : 'Avete il diritto di accesso (art. 15 GDPR), rettifica (art. 16 GDPR), cancellazione (art. 17 GDPR), limitazione del trattamento (art. 18 GDPR), portabilità dei dati (art. 20 GDPR) e opposizione (art. 21 GDPR). Avete inoltre il diritto di presentare un reclamo a un\'autorità di controllo.'}
                </p>

                <p>
                  {language === 'de'
                    ? 'Bei Fragen zum Datenschutz kontaktieren Sie uns bitte unter datenschutz@amitalia.de'
                    : 'Per domande sulla privacy, contattateci all\'indirizzo datenschutz@amitalia.de'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
